import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect } from 'react';
import { matchPath, useParams, useSearchParams, useLocation } from 'react-router';
import { useBundle } from '@amzn/react-arb-tools';
import Alert from '@amzn/meridian/alert';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Column from '@amzn/meridian/column';
import { ReportActionSource } from '@amzn/handshake-painter-typescript-client/v1';
import { RetailRoutes, useRetailRedirectUrls } from 'src/utils/links';
import { getMarketplace } from 'src/utils/marketplace';
import { useAnalyticsContext } from 'src/common/metrics';
import { routes } from 'src/utils/routes';
import { Loading } from '../loaders/Loading';
import { ExternalLink } from '../externalLink';
var DogPageErrorType;
(function (DogPageErrorType) {
    DogPageErrorType["PAYMENT_FAILED"] = "payment_failed";
    DogPageErrorType["GENERAL_ERROR"] = "general_error";
})(DogPageErrorType || (DogPageErrorType = {}));
/**
 * Error page for invalid routing in the application
 * Converted from: https://code.amazon.com/packages/HandshakePainterComponents/blobs/mainline/--/src/kata/templates/error/DogPage.kata
 */
export const DogPage = () => {
    const [bundle, isBundleLoading] = useBundle('pages.dogPage');
    const rumClient = useAnalyticsContext();
    const redirectUrls = useRetailRedirectUrls([RetailRoutes.DETAIL_PAGE, RetailRoutes.ORDER_HISTORY]);
    const { asin } = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const marketplaceId = getMarketplace(searchParams);
    const isPaymentFailed = !!matchPath(routes.paymentFailedPage, location.pathname);
    const errorType = isPaymentFailed ? DogPageErrorType.PAYMENT_FAILED : DogPageErrorType.GENERAL_ERROR;
    // Record analytics on mount
    useEffect(() => {
        rumClient.recordEvent('DOG_PAGE', {
            asin,
            errorType,
            marketplaceId,
        });
    }, []);
    // Helper to create external links with consistent styling
    const createLink = useCallback((href, id, chunk) => (_jsx(ExternalLink, { underline: true, href: href, id: id, source: ReportActionSource.DogPage, children: chunk }, chunk)), []);
    // Determine error content based on error type
    const getErrorContent = useCallback(() => {
        if (isPaymentFailed) {
            return {
                heading: bundle.getMessage('paymentHasNotBeenVerified'),
                message: bundle.formatMessage('bankConnectionFailTryAgainOnAmazon', {
                    a: (chunk) => createLink(redirectUrls.ORDER_HISTORY, 'order-history-link', chunk),
                }),
            };
        }
        if (asin) {
            return {
                heading: bundle.getMessage('somethingWentWrong'),
                message: bundle.formatMessage('tryAgainLaterOrViewOnAmazon', {
                    a: (chunk) => createLink(redirectUrls.DETAIL_PAGE, 'detail-page-link', chunk),
                }),
            };
        }
        return {
            heading: bundle.getMessage('somethingWentWrong'),
            message: bundle.getMessage('tryAgainLater'),
        };
    }, [isPaymentFailed, asin, bundle, createLink, redirectUrls]);
    if (isBundleLoading) {
        return _jsx(Loading, {});
    }
    const { heading, message } = getErrorContent();
    return (_jsxs(Column, { spacingInset: "300 400", spacing: "200", "data-testid": "dog-page", children: [_jsxs(Alert, { type: "error", "data-testid": "error-message", children: [_jsx(Heading, { level: 3, type: "b200", children: _jsx("b", { children: heading }) }), _jsx(Text, { type: "b200", children: _jsx("span", { children: message }) })] }), _jsx("img", { src: "https://m.media-amazon.com/images/G/01/ShopExternal/images/dogs/jake_1_transparent._CB583692400_.png", alt: bundle.getMessage('dogName'), "data-testid": "dog-image" }), _jsx(Text, { type: "b200", alignment: "center", children: bundle.getMessage('dogName') }), _jsx(Text, { type: "b200", alignment: "center", children: _jsx(ExternalLink, { id: "meet-dogs-link", href: "https://www.amazon.com/dogsofamazon", source: ReportActionSource.DogPage, children: bundle.getMessage('meetTheDogsOfAmazon') }) })] }));
};
